/* eslint-disable max-lines-per-function --
   TODO: 招募前 sprint 拆組件 (TODO-frontend-eslint-ci-warnings-as-errors)。
   2026-02-06 規則加入時 87 個既有違規凍結為 baseline，新檔/新函數仍受 hook 攔截。
*/
// frontend/src/components/Search/TagFilter.jsx
// 標籤篩選器元件 - 左側側邊欄

import { useState, useEffect, useMemo } from 'react';
import {
  Paper,
  Typography,
  Box,
  Checkbox,
  FormControlLabel,
  IconButton,
  Collapse,
  Button,
  CircularProgress,
  Alert,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import FilterListIcon from '@mui/icons-material/FilterList';
import ClearIcon from '@mui/icons-material/Clear';
import { getAllTags } from '../../services/tagService';

const TagFilter = ({ selectedTags, onTagChange, searchFacets }) => {
  const [tags, setTags] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expanded, setExpanded] = useState(true);

  // searchFacets 三種 state（D4）：
  //   undefined/null → 未搜尋 / fallback，顯示全域 use_count，不 disable
  //   {} 空物件 → 已搜尋但無匹配，全部顯示 (0) 且 disable
  //   { [id]: n } → 正常 facet 資料
  const hasFacets = searchFacets !== undefined && searchFacets !== null;

  /**
   * 排序規則（D3）：
   *   1. 已選（isSelected）→ 排最前
   *   2. facet > 0 → 按 facet 降序
   *   3. facet === 0 或缺失 → 墊底，按 use_count 降序
   *   4. tiebreaker: id 升序
   * facets 缺失時跳過新排序，維持 API 回傳的原 order (use_count desc)。
   */
  const sortedTags = useMemo(() => {
    if (!hasFacets) return tags;
    const getCount = (tag) => searchFacets[tag.id] ?? 0;
    const isSel = (tag) => selectedTags.includes(tag.id);
    return [...tags].sort((a, b) => {
      const aSel = isSel(a);
      const bSel = isSel(b);
      if (aSel !== bSel) return aSel ? -1 : 1;
      const aCnt = getCount(a);
      const bCnt = getCount(b);
      const aHas = aCnt > 0;
      const bHas = bCnt > 0;
      if (aHas !== bHas) return aHas ? -1 : 1;
      if (aHas) return bCnt - aCnt;
      return (b.use_count || 0) - (a.use_count || 0) || a.id - b.id;
    });
  }, [tags, searchFacets, selectedTags, hasFacets]);

  useEffect(() => {
    loadTags();
  }, []);

  /**
   * 載入所有標籤
   */
  const loadTags = async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await getAllTags({
        sort: 'use_count',
        order: 'desc',
      });
      // API 回傳格式為 { success: true, data: [...] }，需要取出 data 陣列
      if (response.success && Array.isArray(response.data)) {
        setTags(response.data);
      } else {
        setTags([]);
        console.warn('標籤資料格式異常:', response);
      }
    } catch (err) {
      console.error('載入標籤失敗:', err);
      setError('無法載入標籤列表');
    } finally {
      setLoading(false);
    }
  };

  /**
   * 處理標籤選擇
   */
  const handleTagToggle = (tagId) => {
    const newSelectedTags = selectedTags.includes(tagId)
      ? selectedTags.filter((id) => id !== tagId)
      : [...selectedTags, tagId];

    onTagChange(newSelectedTags);
  };

  /**
   * 清除所有選擇
   */
  const handleClearAll = () => {
    onTagChange([]);
  };

  /**
   * 切換展開/收合
   */
  const toggleExpanded = () => {
    setExpanded(!expanded);
  };

  return (
    <Paper
      elevation={2}
      sx={{
        width: '100%',
        maxHeight: '80vh',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {/* 標題列 */}
      <Box
        sx={{
          p: 2,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          borderBottom: expanded ? '1px solid #e0e0e0' : 'none',
          cursor: 'pointer',
        }}
        onClick={toggleExpanded}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <FilterListIcon fontSize="small" color="action" />
          <Typography variant="subtitle1" fontWeight="bold">
            標籤篩選
            {selectedTags.length > 0 && (
              <Typography component="span" variant="caption" color="primary" sx={{ ml: 1 }}>
                (已選 {selectedTags.length} 個)
              </Typography>
            )}
          </Typography>
        </Box>

        <IconButton size="small">{expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}</IconButton>
      </Box>

      {/* 標籤列表 */}
      <Collapse in={expanded}>
        <Box sx={{ p: 2, maxHeight: 'calc(80vh - 80px)', overflow: 'auto' }}>
          {/* 載入中 */}
          {loading && (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress size={30} />
            </Box>
          )}

          {/* 錯誤訊息 */}
          {error && !loading && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {/* 標籤清單 */}
          {!loading && !error && tags.length === 0 && (
            <Typography variant="body2" color="text.secondary" textAlign="center">
              尚無標籤
            </Typography>
          )}

          {!loading && !error && tags.length > 0 && (
            <>
              {sortedTags.map((tag) => {
                const isSelected = selectedTags.includes(tag.id);
                // hasFacets 時 count 來自 facet（缺 key 視為 0）；否則 fallback 到全域 use_count
                const count = hasFacets ? (searchFacets[tag.id] ?? 0) : tag.use_count || 0;
                // 已選項即使 facet=0 也不 disable（維持可取消勾選）
                const isDisabled = hasFacets && !isSelected && count === 0;
                return (
                  <FormControlLabel
                    key={tag.id}
                    disabled={isDisabled}
                    control={
                      <Checkbox
                        checked={isSelected}
                        onChange={() => handleTagToggle(tag.id)}
                        size="small"
                      />
                    }
                    label={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="body2">{tag.name}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          ({count})
                        </Typography>
                      </Box>
                    }
                    sx={{
                      display: 'flex',
                      width: '100%',
                      ml: 0,
                      mb: 0.5,
                      opacity: isDisabled ? 0.5 : 1,
                      '&:hover': {
                        backgroundColor: isDisabled ? 'transparent' : '#f5f5f5',
                        borderRadius: 1,
                      },
                    }}
                  />
                );
              })}

              {/* 清除按鈕 */}
              {selectedTags.length > 0 && (
                <Button
                  fullWidth
                  size="small"
                  startIcon={<ClearIcon />}
                  onClick={handleClearAll}
                  sx={{ mt: 2 }}
                >
                  清除所有篩選
                </Button>
              )}
            </>
          )}
        </Box>
      </Collapse>
    </Paper>
  );
};

export default TagFilter;
